import CloseIcon from '@mui/icons-material/Close';
import FunctionsIcon from '@mui/icons-material/Functions';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ModeEditOutline from '@mui/icons-material/ModeEditOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import useScenarioMode from '../../../../shared/hooks/useScenarioMode';
import { formatNumberDE } from '../../../../shared/utils/formatNumber';

/** Thousand-separated number, or the given fallback for null/undefined. */
const fmt = (value, fallback = '?') =>
  value == null ? fallback : formatNumberDE(value);

/** Check whether a stock row is projected (i.e. actual inflow not yet available) */
const isProjectedDate = (actualInflow) => {
  return actualInflow == null;
};

/** Format a date string to DD-MMM-YYYY (e.g. 02-Mar-2026). Returns "—" for falsy values. */
const formatDate = (value) =>
  value ? dayjs(value).format('DD-MMM-YYYY') : '—';

/** Tooltip content that shows a formula breakdown */
const FormulaTooltip = ({ formula }) => {
  if (!formula) return null;
  return (
    <Box sx={{ p: 0.5, maxWidth: 260 }}>
      <Stack direction='row' alignItems='center' spacing={0.5} mb={0.5}>
        <FunctionsIcon sx={{ fontSize: '0.8rem', opacity: 0.7 }} />
        <Typography
          variant='caption'
          fontWeight='bold'
          sx={{
            fontSize: '0.65rem',
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}
        >
          How it's calculated
        </Typography>
      </Stack>
      {formula.expression && (
        <Typography
          variant='caption'
          component='div'
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.7rem',
            backgroundColor: 'rgba(255,255,255,0.08)',
            borderRadius: 0.5,
            px: 0.75,
            py: 0.5,
            mb: 0.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {formula.expression}
        </Typography>
      )}
      {formula.substitution && (
        <Typography
          variant='caption'
          component='div'
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.7rem',
            backgroundColor: 'rgba(255,255,255,0.04)',
            borderRadius: 0.5,
            px: 0.75,
            py: 0.5,
            mb: 0.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {formula.substitution}
        </Typography>
      )}
      {formula.note && (
        <Typography
          variant='caption'
          sx={{ fontSize: '0.6rem', opacity: 0.7, fontStyle: 'italic' }}
        >
          {formula.note}
        </Typography>
      )}
    </Box>
  );
};

/** Compact summary card for a single stock metric — hover to see formula */
const MetricCard = ({ label, value, color, formula, primary, subtitle }) => {
  const card = (
    <Box
      sx={{
        flex: 1,
        px: 1,
        py: primary ? 0.85 : 0.6,
        borderRadius: 0.5,
        minWidth: 0,
        cursor: formula ? 'help' : 'default',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(0,0,0,0.03)',
        transition: 'background-color 0.15s',
        '&:hover': formula
          ? {
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.09)'
                  : 'rgba(0,0,0,0.06)',
            }
          : {},
      }}
    >
      <Stack direction='row' alignItems='center' spacing={0.25}>
        <Typography
          variant='caption'
          color='text.secondary'
          noWrap
          sx={{ fontSize: '0.7rem', lineHeight: 1.2, flex: 1 }}
        >
          {label}
        </Typography>
        {formula && (
          <FunctionsIcon
            sx={{ fontSize: '0.6rem', color: 'text.disabled', flexShrink: 0 }}
          />
        )}
      </Stack>
      <Typography
        variant={primary ? 'body1' : 'body2'}
        fontWeight='bold'
        color={color ?? 'text.primary'}
        noWrap
        sx={{ lineHeight: 1.3 }}
      >
        {fmt(value, '—')}
      </Typography>
      {subtitle && (
        <Typography
          variant='caption'
          color='text.secondary'
          noWrap
          sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.8 }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  if (!formula) return card;

  return (
    <Tooltip
      title={<FormulaTooltip formula={formula} />}
      arrow
      placement='top'
      enterDelay={200}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 300,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'grey.900' : 'grey.800',
          },
        },
      }}
    >
      {card}
    </Tooltip>
  );
};

/** Section wrapper that groups related metrics with a colored left border and label */
const MetricGroup = ({ title, icon, borderColor, children }) => (
  <Box
    sx={{
      borderLeft: 3,
      borderColor,
      borderRadius: '0 4px 4px 0',
      pl: 1,
      mb: 1.25,
    }}
  >
    <Stack direction='row' alignItems='center' spacing={0.5} mb={0.5}>
      {icon}
      <Typography
        variant='caption'
        fontWeight='bold'
        color='text.secondary'
        sx={{
          fontSize: '0.6rem',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {title}
      </Typography>
    </Stack>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 0.75,
      }}
    >
      {children}
    </Box>
  </Box>
);

/** Prominent banner card for a single highlighted metric (e.g. Bottleneck) */
const HighlightBanner = ({ label, value, color, bgColor, formula }) => {
  const banner = (
    <Box
      sx={{
        px: 1.25,
        py: 0.85,
        mb: 1,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: formula ? 'help' : 'default',
        transition: 'opacity 0.15s',
        '&:hover': formula ? { opacity: 0.85 } : {},
        backgroundColor:
          bgColor ??
          ((theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.06)'
              : 'rgba(0,0,0,0.04)'),
      }}
    >
      <Stack direction='row' alignItems='center' spacing={0.5}>
        <Typography
          variant='caption'
          fontWeight='bold'
          color='text.secondary'
          sx={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}
        >
          {label}
        </Typography>
        {formula && (
          <FunctionsIcon sx={{ fontSize: '0.65rem', color: 'text.disabled' }} />
        )}
      </Stack>
      <Typography
        variant='body1'
        fontWeight='bold'
        color={color ?? 'text.primary'}
      >
        {fmt(value, '—')}
      </Typography>
    </Box>
  );

  if (!formula) return banner;

  return (
    <Tooltip
      title={<FormulaTooltip formula={formula} />}
      arrow
      placement='top'
      enterDelay={200}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 300,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'grey.900' : 'grey.800',
          },
        },
      }}
    >
      {banner}
    </Tooltip>
  );
};

const StockVoyageSidePanel = ({
  row,
  allRows = [],
  onClose,
  onEditClicked,
}) => {
  const { isScenarioViewOnly } = useScenarioMode();
  if (!row) return null;

  // Build date lookup to resolve d-1 / d-2 references with carry-forward
  // Normalize all dates to YYYY-MM-DD so they match the output of getOffsetDate
  const normalizeDate = (dateStr) => dateStr?.split('T')[0] ?? null;
  const rowByDate = Object.fromEntries(
    allRows
      .filter((r) => normalizeDate(r.date) != null)
      .map((r) => [normalizeDate(r.date), r])
  );
  const rowByDateActual = Object.fromEntries(
    allRows
      .filter((r) => r.actualInflow != null && normalizeDate(r.date) != null)
      .map((r) => [normalizeDate(r.date), r])
  );
  const sortedDates = allRows
    .map((r) => normalizeDate(r.date))
    .filter(Boolean)
    .sort()
    .reverse();
  const sortedDatesActual = Object.keys(rowByDateActual).sort().reverse();

  const getOffsetDate = (dateStr, offsetDays) => {
    if (!dateStr) return null;
    const d = new Date(dateStr.split('T')[0] + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + offsetDays);
    return d.toISOString().split('T')[0];
  };

  const makeCarryForwardFinder = (byDate, descDates) => (targetDate) => {
    if (!targetDate) return { row: null, exact: false };
    if (byDate[targetDate]) return { row: byDate[targetDate], exact: true };
    let best = null;
    for (const d of descDates) {
      if (d < targetDate) {
        best = d;
        break;
      }
    }
    return { row: best ? byDate[best] : null, exact: false };
  };

  const findRowOrCarryForward = makeCarryForwardFinder(rowByDate, sortedDates);
  const findActualRowOrCarryForward = makeCarryForwardFinder(
    rowByDateActual,
    sortedDatesActual
  );

  const d1Result = findRowOrCarryForward(getOffsetDate(row.date, -1));
  const d2Result = findRowOrCarryForward(getOffsetDate(row.date, -2));
  const d1ActualResult = findActualRowOrCarryForward(
    getOffsetDate(row.date, -1)
  );

  const rowD1 = d1Result.row;
  const rowD2 = d2Result.row;
  const rowActualD1 = d1ActualResult.row;

  // Map voyages from flat VoyageSummaryDTO shape
  const voyages = (row.voyages ?? []).map((v) => ({
    voyageId: v.voyageId,
    voyageNumber: v.voyageNumber ?? '—',
    shipName: v.shipName ?? null,
    shipCompanyName: v.shipCompanyName ?? null,
    totalShipped: v.totalShipped ?? 0,
  }));

  // Total shipped per voyage — pre-aggregated in the flat DTO
  const getVoyageShipped = (voyage) => voyage.totalShipped ?? 0;

  const bottleneckColor =
    row.bottleneck == null
      ? undefined
      : row.bottleneck < 0
        ? 'error.main'
        : row.bottleneck > 0
          ? 'success.main'
          : undefined;

  return (
    <Paper
      elevation={1}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          pt: 1,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)'
              : 'linear-gradient(135deg, rgba(0,120,212,0.06) 0%, rgba(0,120,212,0.01) 100%)',
        }}
      >
        {/* Top: status chips + close */}
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={0.5}
        >
          <Stack direction='row' alignItems='center' spacing={0.5}>
            {isProjectedDate(row.actualInflow) && (
              <Chip
                label='Projected'
                size='small'
                color='warning'
                variant='outlined'
                sx={{ height: 20, fontSize: '0.6rem', fontWeight: 'bold' }}
              />
            )}
            <Tooltip
              arrow
              placement='bottom'
              title='Ready for Shipment − Total Volume'
            >
              <Chip
                label={`Bottleneck: ${fmt(row.bottleneck, '—')}`}
                size='small'
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  cursor: 'help',
                  bgcolor:
                    row.bottleneck == null
                      ? 'action.selected'
                      : row.bottleneck < 0
                        ? 'error.main'
                        : row.bottleneck > 0
                          ? 'success.main'
                          : 'action.selected',
                  color:
                    row.bottleneck != null && row.bottleneck !== 0
                      ? '#fff'
                      : 'text.primary',
                }}
              />
            </Tooltip>
          </Stack>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Stack>

        {/* 4 rows: label left — value right */}
        <Stack spacing={0.5}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography variant='caption' color='text.secondary'>
              Actual ETD
            </Typography>
            <Typography variant='caption' fontWeight='bold'>
              {formatDate(row.date)}
            </Typography>
          </Stack>
          {/* <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Market Country
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {marketCountryLabel ?? "—"}
            </Typography>
          </Stack> */}
          {/* <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Route
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {row.departurePortId ?? "—"} → {row.destinationPortId ?? "—"}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Market Country
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {row.marketName
                ? `(${row.marketId}) ${row.marketName}`
                : (row.marketId ?? "—")}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="caption" color="text.secondary">
              Port Operator
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {row.portOperatorName ?? row.portOperatorId ?? "—"}
            </Typography>
          </Stack> */}
        </Stack>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.25 }}>
        {/* Voyage / Shipment list */}
        <Stack direction='row' alignItems='center' spacing={0.5} mb={0.5}>
          <Typography
            variant='caption'
            fontWeight='bold'
            color='text.secondary'
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Voyages &amp; Shipments
          </Typography>
          {voyages.length > 0 && (
            <Chip
              label={`${voyages.length}`}
              size='small'
              color='default'
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 'bold',
                minWidth: 20,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
          <Tooltip
            arrow
            placement='top'
            title={
              <Box sx={{ p: 0.5, maxWidth: 260 }}>
                <Typography
                  variant='caption'
                  fontWeight='bold'
                  sx={{ fontSize: '0.65rem' }}
                >
                  Voyage Matching Rules
                </Typography>
                <Typography
                  variant='caption'
                  component='div'
                  sx={{ fontSize: '0.6rem', mt: 0.5, fontFamily: 'monospace' }}
                >
                  market = stock.market{'\n'}
                  departure_port = stock.departure_port{'\n'}
                  destination_port = stock.destination_port{'\n'}
                  port_operator = stock.port_operator{'\n'}
                  DATE(actual_etd) = stock.date
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: '0.6rem',
                    opacity: 0.7,
                    fontStyle: 'italic',
                    mt: 0.5,
                  }}
                  component='div'
                >
                  Voyages matching all 5 criteria appear below with their
                  shipments.
                </Typography>
              </Box>
            }
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: '0.75rem',
                color: 'text.disabled',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Stack>

        {voyages.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
            No planned voyages for this stock row.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 'bold', py: 0.4, fontSize: '0.7rem' }}
                    >
                      Ship Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold', py: 0.4, fontSize: '0.7rem' }}
                    >
                      Shipping Company
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold', py: 0.4, fontSize: '0.7rem' }}
                      align='right'
                    >
                      <Tooltip
                        arrow
                        placement='top'
                        title='Total shipped units for this voyage (sum of shipmentByMarketCountry)'
                      >
                        <Typography
                          component='span'
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            cursor: 'help',
                            borderBottom: '1px dashed',
                            borderColor: 'text.disabled',
                          }}
                        >
                          Shipped
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    {onEditClicked && (
                      <TableCell
                        sx={{ fontWeight: 'bold', py: 0.4, fontSize: '0.7rem' }}
                        align='right'
                      />
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voyages.map((voyage) => (
                    <TableRow key={voyage.voyageId}>
                      <TableCell sx={{ py: 0.4 }}>
                        <Typography variant='caption' fontWeight='medium'>
                          {voyage.shipName ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.4 }}>
                        <Typography variant='caption' color='text.secondary'>
                          {voyage.shipCompanyName ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align='right' sx={{ py: 0.4 }}>
                        <Typography variant='caption' fontWeight='medium'>
                          {fmt(getVoyageShipped(voyage))}
                        </Typography>
                      </TableCell>
                      {onEditClicked && (
                        <TableCell align='right' sx={{ py: 0.4 }}>
                          <Tooltip title={isScenarioViewOnly ? 'View' : 'Edit'}>
                            <IconButton
                              size='small'
                              onClick={() => onEditClicked(voyage.voyageId)}
                            >
                              {isScenarioViewOnly ? (
                                <VisibilityOutlined fontSize='inherit' />
                              ) : (
                                <ModeEditOutline fontSize='inherit' />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        <Box sx={{ my: 1.25 }} />
        {/* ── Inventory group ── */}

        <Typography
          variant='caption'
          fontWeight='bold'
          color='text.secondary'
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Stock Summary
        </Typography>

        <MetricGroup
          title='Inventory'
          borderColor='info.main'
          icon={
            <InventoryIcon sx={{ fontSize: '0.7rem', color: 'info.main' }} />
          }
        >
          <MetricCard
            label='Current Stock'
            value={row.currentStock}
            primary
            formula={{
              expression: isProjectedDate(row.actualInflow)
                ? 'Previous Day Stock + Forecast Inflow - Previous Day Total Volume'
                : undefined,
              substitution: isProjectedDate(row.actualInflow)
                ? `${fmt(rowD1?.currentStock ?? 0)} + ${fmt(row.forecastInflow ?? 0)} - ${fmt(rowD1?.shippedSum ?? 0)} = ${fmt(row.currentStock)}`
                : undefined,
              note: isProjectedDate(row.actualInflow)
                ? 'Projected forward from last known stock (up to 6 weeks)'
                : 'Actual on-hand vehicles from port operator feed',
            }}
          />
          <MetricCard
            label='Total Blocked Cars'
            value={row.totalBlockedCars}
            formula={(() => {
              const defaultNote =
                'Sum of blocked volumes across all hold types (provided by backend)';
              if (row.actualInflow == null) {
                const sourceRow = findActualRowOrCarryForward(
                  normalizeDate(row.date)
                )?.row;
                if (sourceRow) {
                  return {
                    note: `Blocked volumes are carried forward from ${formatDate(sourceRow.date)}.`,
                  };
                }
              }
              return { note: defaultNote };
            })()}
          />
          <MetricCard
            label='Opening Inventory'
            value={row.openingInventory}
            formula={{
              expression: 'Current Stock − (Actual Inflow ?? Forecast Inflow)',
              substitution: `${fmt(row.currentStock)} − ${fmt(row.actualInflow ?? row.forecastInflow)} = ${fmt(row.openingInventory)}`,
              note:
                row.actualInflow != null
                  ? 'Using actual inflow (available for this date)'
                  : 'Using forecast inflow (actual not yet available)',
            }}
          />
        </MetricGroup>

        {/* ── Inflow group ── */}
        <MetricGroup
          title='Inflow'
          borderColor='warning.main'
          icon={
            <TrendingUpIcon
              sx={{ fontSize: '0.7rem', color: 'warning.main' }}
            />
          }
        >
          <MetricCard
            label='Actual Inflow'
            value={row.actualInflow}
            subtitle={
              row.differenceInflow != null
                ? `${row.differenceInflow > 0 ? '▲' : row.differenceInflow < 0 ? '▼' : '='} ${fmt(Math.abs(row.differenceInflow))} vs forecast`
                : undefined
            }
            color={
              row.actualInflow == null
                ? 'text.secondary'
                : row.differenceInflow > 0
                  ? 'success.main'
                  : row.differenceInflow < 0
                    ? 'error.main'
                    : undefined
            }
            formula={{
              expression: 'Diff = (Actual ?? 0) − Forecast',
              substitution: `${fmt(row.actualInflow ?? 0)} − ${fmt(row.forecastInflow)} = ${fmt(row.differenceInflow)}`,
              note: 'Positive diff = more arrivals than planned',
            }}
          />
          <MetricCard
            label='Forecast Inflow'
            value={row.forecastInflow}
            formula={{
              note: 'Planned vehicle arrivals for this date (provided by backend)',
            }}
          />
        </MetricGroup>

        {/* ── Shipment group (includes Ready for Shipment) ── */}
        <MetricGroup
          title='Shipment'
          borderColor='success.main'
          icon={
            <LocalShippingIcon
              sx={{ fontSize: '0.7rem', color: 'success.main' }}
            />
          }
        >
          <MetricCard
            label='Ready for Shipment'
            value={row.readyForShipment}
            primary
            formula={(() => {
              const csD2 = rowD2?.currentStock ?? 0;
              const ssD1 = rowD1?.shippedSum ?? 0;
              const blD1 = rowActualD1?.totalBlockedCars ?? 0;
              const noteLines = [];
              if (rowD2) {
                noteLines.push(
                  d2Result.exact
                    ? `Stock from ${formatDate(rowD2.date)}`
                    : `Stock carried forward from ${formatDate(rowD2.date)} (exact d−2 not in dataset)`
                );
              } else {
                noteLines.push('No prior stock row available — using 0');
              }
              if (rowD1) {
                noteLines.push(
                  d1Result.exact
                    ? `Total Volume from ${formatDate(rowD1.date)}`
                    : `Total Volume carried forward from ${formatDate(rowD1.date)} (exact d−1 not in dataset)`
                );
              } else {
                noteLines.push('No prior total volume row available — using 0');
              }
              if (rowActualD1) {
                noteLines.push(
                  d1ActualResult.exact
                    ? `Blocked from ${formatDate(rowActualD1.date)}`
                    : `Blocked carried forward from ${formatDate(rowActualD1.date)} (exact d−1 not in dataset)`
                );
              } else {
                noteLines.push('No prior blocked row available — using 0');
              }
              return {
                expression:
                  'Current Stock[d−2] − Total Volume[d−1] − Blocked[d−1]',
                substitution: `${fmt(csD2)} − ${fmt(ssD1)} − ${fmt(blD1)} = ${fmt(row.readyForShipment)}`,
                note: noteLines.join('. '),
              };
            })()}
          />
          <MetricCard
            label='Total Volume'
            value={row.shippedSum}
            primary
            subtitle={
              voyages.length > 0
                ? `${voyages.length} voyage${voyages.length !== 1 ? 's' : ''}`
                : undefined
            }
            formula={{
              expression: 'Σ shipmentByMarketCountry per voyage',
              substitution:
                voyages.length > 0
                  ? voyages.map((v) => fmt(v.totalShipped ?? 0)).join(' + ') +
                    ` = ${fmt(row.shippedSum)}`
                  : undefined,
              note: 'Sum of shipmentByMarketCountry across all voyages and market countries.',
            }}
          />
        </MetricGroup>

        {/* ── Bottleneck — conclusion banner at the end ── */}
        <HighlightBanner
          label='Bottleneck'
          value={row.bottleneck}
          color={bottleneckColor}
          formula={{
            expression: 'Ready for Shipment − Total Volume',
            substitution: `${fmt(row.readyForShipment)} − ${fmt(row.shippedSum)} = ${fmt(row.bottleneck)}`,
            note: 'Positive = surplus capacity, Negative = overbooked',
          }}
          bgColor={(theme) =>
            row.bottleneck == null
              ? theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.04)'
              : row.bottleneck < 0
                ? theme.palette.mode === 'dark'
                  ? 'rgba(244,67,54,0.12)'
                  : 'rgba(244,67,54,0.08)'
                : row.bottleneck > 0
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(76,175,80,0.12)'
                    : 'rgba(76,175,80,0.08)'
                  : theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.04)'
          }
        />
      </Box>
    </Paper>
  );
};

export default StockVoyageSidePanel;
